import { Capacitor } from '@capacitor/core';

/**
 * Local key-value storage backing the file-level outbox.
 *
 * Native (Capacitor) → @capacitor/filesystem under Directory.Data
 *   (= Library/NoCloud — excluded from iCloud backups by default, so
 *   offline notes never silently leak to Apple iCloud).
 * Web               → IndexedDB.
 *
 * Both expose the same `get` / `set` / `del` / `keys` API.  Keys are
 * percent-encoded so repo-relative paths (which contain `/`) map to legal
 * filesystem keys.
 */

const IS_NATIVE = Capacitor.isNativePlatform();
const BASE_DIR = 'outbox';

/** Encode an arbitrary key (repo-relative path) into a storage-safe key. */
export function encodeKey(raw) {
  return encodeURIComponent(raw);
}

/** Decode a storage key back into its original form (best-effort). */
export function decodeKey(encoded) {
  try {
    return decodeURIComponent(encoded);
  } catch {
    return encoded;
  }
}

// ── Web backend: IndexedDB ────────────────────────────────────────

let _dbPromise = null;

function _openDb() {
  if (!_dbPromise) {
    _dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open('julynote-outbox', 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('kv')) {
          db.createObjectStore('kv');
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return _dbPromise;
}

async function _idbGet(key) {
  const db = await _openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('kv', 'readonly');
    const req = tx.objectStore('kv').get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function _idbSet(key, value) {
  const db = await _openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('kv', 'readwrite');
    tx.objectStore('kv').put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function _idbDel(key) {
  const db = await _openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('kv', 'readwrite');
    tx.objectStore('kv').delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function _idbKeys() {
  const db = await _openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('kv', 'readonly');
    const req = tx.objectStore('kv').getAllKeys();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

// ── Native backend: @capacitor/filesystem (lazy-loaded) ───────────

let _fsPromise = null;

function _getFs() {
  if (!_fsPromise) {
    _fsPromise = import('@capacitor/filesystem');
  }
  return _fsPromise;
}

async function _nativeGet(key) {
  const fs = await _getFs();
  try {
    const { Filesystem, Directory, Encoding } = fs;
    const res = await Filesystem.readFile({
      path: `${BASE_DIR}/${key}`,
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
    return JSON.parse(res.data);
  } catch (e) {
    // Not found (or any other read failure) → miss.
    return undefined;
  }
}

async function _nativeSet(key, value) {
  const fs = await _getFs();
  const { Filesystem, Directory, Encoding } = fs;
  await Filesystem.writeFile({
    path: `${BASE_DIR}/${key}`,
    data: JSON.stringify(value),
    directory: Directory.Data,
    encoding: Encoding.UTF8,
    recursive: true,
  });
}

async function _nativeDel(key) {
  const fs = await _getFs();
  const { Filesystem, Directory } = fs;
  try {
    await Filesystem.deleteFile({
      path: `${BASE_DIR}/${key}`,
      directory: Directory.Data,
    });
  } catch {
    // Already gone — no-op.
  }
}

async function _nativeKeys() {
  const fs = await _getFs();
  const { Filesystem, Directory } = fs;
  try {
    const res = await Filesystem.readdir({
      path: BASE_DIR,
      directory: Directory.Data,
    });
    return (res.files || []).map((f) => f.name);
  } catch {
    return [];
  }
}

// ── Public API ─────────────────────────────────────────────────────

export async function storageGet(key) {
  if (IS_NATIVE) return _nativeGet(key);
  return _idbGet(key);
}

export async function storageSet(key, value) {
  if (IS_NATIVE) return _nativeSet(key, value);
  return _idbSet(key, value);
}

export async function storageDel(key) {
  if (IS_NATIVE) return _nativeDel(key);
  return _idbDel(key);
}

export async function storageKeys() {
  if (IS_NATIVE) return _nativeKeys();
  return _idbKeys();
}
